import { Maybe } from '../../../../types/globals';
import { Currency } from '../../../common/types/config';
import { Logger } from '../../../common/types/loggerTypes';
import {
    DataLayerItem,
    GenericEventOptions,
    GENERIC_GA_EVENT,
    SpecificGaEvent,
    SpecificEventOptions,
    START_USER_SESSION,
} from '../types';
import { DataLayerPlatform } from './serverDataLayerManager';

export interface DataLayerManagerInterfaceReadOnly {
    getDataLayer(): DataLayerItem[];
    getDelayedEvents(): DataLayerItem[];
    getUserSessionDidInit(): boolean;
    getRegisteredEvents(): DataLayerItem[];
}

export interface UserSessionInfo extends DataLayerItem {
    'user-id': string;
    'user-name': string;
    'user-email': string;
    'user-createdAt': string;
    'user-hashed-mail': string;
    'hashed-user-id': string;
    'user-login_check': string;
}

export interface DataLayerManagerInterface extends DataLayerManagerInterfaceReadOnly {
    initUserSession(info?: Maybe<UserSessionInfo>): this;
    sendData(data: DataLayerItem): this;
    sendEvent(event: string, options: DataLayerItem): this;
    sendGenericEvent(options: GenericEventOptions): this;
    sendSpecificEvent(event: SpecificGaEvent, options: SpecificEventOptions): this;
    setPlatform(platform: DataLayerPlatform): this;
}

abstract class AbstractDataLayerManager implements DataLayerManagerInterface {
    private readonly logger: Logger;

    protected userSessionDidInit: boolean = false;

    protected delayedEvents: DataLayerItem[] = [];

    protected abstract getRawDataLayer(): DataLayerItem[];

    public abstract setPlatform(platform: DataLayerPlatform): this;

    public currency: Currency;

    protected addToDelayedEvents(data: DataLayerItem): this {
        this.delayedEvents.push(data);
        return this;
    }

    public getDataLayer(): DataLayerItem[] {
        return [...this.getRawDataLayer()];
    }

    protected getLogger(): Logger {
        return this.logger;
    }

    protected push(data: DataLayerItem): this {
        this.getRawDataLayer().push(data);
        return this;
    }

    protected processAllDelayedEvents(): this {
        this.getDelayedEvents().forEach(this.push.bind(this));
        this.delayedEvents = [];
        return this;
    }

    public constructor(logger: Logger) {
        this.logger = logger;
    }

    public getDelayedEvents(): DataLayerItem[] {
        return [...this.delayedEvents];
    }

    public getRegisteredEvents(): DataLayerItem[] {
        return [...this.getDataLayer(), ...this.getDelayedEvents()];
    }

    public getUserSessionDidInit(): boolean {
        return this.userSessionDidInit;
    }

    public initUserSession(userInfo?: Maybe<UserSessionInfo>): this {
        this.userSessionDidInit = true;
        if (userInfo) {
            this.sendData(userInfo);
        }
        this.sendSpecificEvent(START_USER_SESSION, {});
        return this.processAllDelayedEvents();
    }

    public sendData(data: DataLayerItem): this {
        if (!this.getUserSessionDidInit()) {
            return this.addToDelayedEvents(data);
        }
        return this.push(data);
    }

    public sendEvent(event: string, options: DataLayerItem): this {
        this.sendData({
            event,
            ...options,
        });
        return this;
    }

    public sendGenericEvent(options: GenericEventOptions): this {
        if (options.label) {
            this.sendEvent(GENERIC_GA_EVENT, <DataLayerItem>options);
        }
        return this;
    }

    public sendSpecificEvent(event: SpecificGaEvent, options: SpecificEventOptions): this {
        return this.sendEvent(event, <DataLayerItem>options);
    }
}

export default AbstractDataLayerManager;
